import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Edit, AlertTriangle } from 'lucide-react';
import { Recipe } from '../../../../services/recipes/recipesService';
import { AllergenDraft, draftFromRecipe, getUnmappedLegacyAllergens, isSupportedAllergenDetails } from '../../../../types/RecipeAllergens';
import { allergenErrorKey } from '../../../../services/recipes/recipeAllergensService';
import AllergenDeclarationEditor, { LegacyAllergenWarnings } from '../../../recipes/AllergenDeclarationEditor';

import RecipeAllergenSummary from '../../../recipes/RecipeAllergenSummary';

interface Props {
  recipe: Recipe;
  onSave: (recipeId: string, revision: string, declaration: AllergenDraft) => Promise<void>;
  onReload: () => Promise<void>;
}

export default function RecipeAllergiesSection({ recipe, onSave, onReload }: Props) {
  const { t } = useTranslation();
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState(() => draftFromRecipe(recipe));
  // Capture the revision when editing starts; background refresh cannot advance it.
  const [revision, setRevision] = useState('');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const supported = isSupportedAllergenDetails(recipe.allergenDetails);
  const hasUnmappedAllergens = getUnmappedLegacyAllergens(recipe.allergies).length > 0;

  const save = async () => {
    setSaving(true);
    setError(null);
    try { await onSave(recipe.id, revision, draft); setEditing(false); }
    catch (error) { setError(allergenErrorKey(error)); }
    finally { setSaving(false); }
  };

  return (
    <section className="bg-white dark:bg-background-dark rounded-xl border border-[#E5E7EB] dark:border-borderPrimary-dark p-4 h-full">
      <div className="flex items-center justify-between mb-3 gap-2">
        <h2 className="flex items-center gap-2 text-base font-semibold text-zinc-900 dark:text-zinc-100">
          <AlertTriangle className="w-4 h-4 text-amber-600 dark:text-amber-400" />{t('recipes.allergies')}
        </h2>
        {!editing && supported && !hasUnmappedAllergens && <button type="button" aria-label={t('common.edit')} className="p-2 rounded-lg text-zinc-600 dark:text-zinc-300 hover:bg-zinc-100 dark:hover:bg-zinc-800"
          onClick={() => { setDraft(draftFromRecipe(recipe)); setRevision(recipe.allergenDetails!.revision); setError(null); setEditing(true); }}>
          <Edit className="w-4 h-4" />
        </button>}
      </div>
      {editing ? <>
        <LegacyAllergenWarnings allergies={recipe.allergies} />
        <AllergenDeclarationEditor value={draft} onChange={setDraft} disabled={saving || hasUnmappedAllergens} />
        {error && <div role="alert" className="my-3 text-sm text-red-700 dark:text-red-300">
          <p>{t(error)}</p>
          <button type="button" className="underline py-2" onClick={async () => { await onReload(); setEditing(false); setError(null); }}>{t('recipeAllergens.reload')}</button>
        </div>}
        <div className="mt-3 flex flex-wrap gap-2">
          <button type="button" disabled={saving} onClick={() => setEditing(false)} className="px-4 py-2 rounded-lg border border-zinc-300 dark:border-zinc-600 text-zinc-700 dark:text-zinc-300">{t('common.cancel')}</button>
          <button type="button" disabled={saving || hasUnmappedAllergens} onClick={save} className="px-4 py-2 rounded-lg bg-emerald-700 dark:bg-emerald-700 text-white dark:text-white disabled:opacity-50">{t(saving ? 'common.saving' : 'common.save')}</button>
        </div>
      </> : <RecipeAllergenSummary recipe={recipe} />}
    </section>
  );
}
